import {
  MixinOperandType,
  MixinOperationType,
  MixinSyntaxTree,
} from '@upright/common/model/input/product-mixins/types';
import { parseMixin } from '@upright/common/model/input/product-mixins';
import { applyArgumentsToTemplate } from '@upright/common/model/input/product-mixins/templates';
import { MixinTemplate } from '@upright/common/model/input/product-mixins/templates/types';
import { CertificateType } from '@upright/common/model/input/companies/products/ipr';

export interface LogicalExpressionToMixinSyntaxTreeOptions {
  /**
   * If true, throws an error when a mapping is not found for industry or energy source.
   * If false (default), returns the base product when mapping is not found.
   */
  strict?: boolean;
}
import { LogicalMixinExpression, ExpressionType } from './types';
import { Dictionary } from 'lodash';

/**
 * Static mappings from certificate types to template expressions.
 */
const CERTIFICATE_TO_TEMPLATE_EXPRESSION: Partial<Record<CertificateType, string>> = {
  [CertificateType.FSC]: 'FSC certified X',
  [CertificateType.PEFC]: 'PEFC certified X',
};

/**
 * Template expression for ForDisabledPeople.
 */
const FOR_DISABLED_PEOPLE_TEMPLATE_EXPRESSION = 'X for disabled people';

/**
 * Creates a MixinOperand representing a product.
 */
function createProductOperand(productCode: string): MixinSyntaxTree {
  return {
    type: MixinOperandType.PRODUCT,
    arguments: [productCode],
  };
}

/**
 * Creates a MixinOperation with the given type and arguments.
 */
function createOperation(type: MixinOperationType, args: MixinSyntaxTree[]): MixinSyntaxTree {
  return {
    type,
    arguments: args,
  };
}

/**
 * Converts a PureProductExpression to a MixinSyntaxTree.
 */
function convertPureProduct(
  expression: LogicalMixinExpression,
  _templates: Dictionary<MixinTemplate>,
  _options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.PureProduct) {
    throw new Error(`Expected PureProduct expression, got ${expression.type}`);
  }
  const productCode = expression.productCode;
  if (!productCode) {
    throw new Error(`Product code not found for PureProduct expression: ${JSON.stringify(expression)}`);
  }
  return createProductOperand(productCode);
}

/**
 * Converts a CombinationExpression to a MixinSyntaxTree using the 'and' operation.
 */
function convertCombination(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.Combination) {
    throw new Error(`Expected Combination expression, got ${expression.type}`);
  }

  const convertedCombinations = expression.combinations.map(expr =>
    logicalMixinExpressionToMixinSyntaxTree(expr, templates, options),
  );

  if (convertedCombinations.length === 0) {
    throw new Error(`No combinations found`);
  }

  if (convertedCombinations.length === 1) {
    return convertedCombinations[0];
  }

  return createOperation(MixinOperationType.AND, convertedCombinations);
}

/**
 * Recursively processes industry MixinSyntaxTrees.
 * The productCode is already set in the expression from ipr-to-logical-expressions.ts.
 * For AND operations, reduces to single operand if only one remains.
 */
function processIndustryTree(tree: MixinSyntaxTree): MixinSyntaxTree {
  if (tree.type === MixinOperandType.PRODUCT) {
    // productCode is already set correctly from ipr-to-logical-expressions.ts
    return tree;
  }

  if (tree.type === MixinOperationType.AND) {
    const processedArgs = tree.arguments.map(arg => processIndustryTree(arg as MixinSyntaxTree));
    if (processedArgs.length === 0) {
      throw new Error(`No arguments found`);
    }
    if (processedArgs.length === 1) {
      return processedArgs[0];
    }
    return {
      ...tree,
      arguments: processedArgs,
    };
  }

  return tree;
}

/**
 * Converts a ForIndustryExpression to a MixinSyntaxTree using the 'v2-for' operation.
 * Uses the template: "X for Y" which translates to "X v2-for Y"
 */
function convertForIndustry(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.ForIndustry) {
    throw new Error(`Expected ForIndustry expression, got ${expression.type}`);
  }

  const baseProduct = logicalMixinExpressionToMixinSyntaxTree(expression.baseProduct, templates, options);
  const forProduct = logicalMixinExpressionToMixinSyntaxTree(expression.forProduct, templates, options);
  const processedForProduct = processIndustryTree(forProduct);

  return createOperation(MixinOperationType.V2_FOR, [baseProduct, processedForProduct]);
}

/**
 * Converts a CertificateExpression to a MixinSyntaxTree using certificate templates from the database.
 */
function convertCertificate(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.Certificate) {
    throw new Error(`Expected Certificate expression, got ${expression.type}`);
  }

  const baseProduct = logicalMixinExpressionToMixinSyntaxTree(expression.baseProduct, templates, options);

  // Get the template expression for this certificate type
  const templateExpression = CERTIFICATE_TO_TEMPLATE_EXPRESSION[expression.certificate];
  if (!templateExpression) {
    return baseProduct; // Return as-is if no template found
  }

  const template = templates[templateExpression];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!template) {
    return baseProduct; // Return as-is if template not loaded
  }

  // Get the product code from the base product
  const productCode = baseProduct.type === MixinOperandType.PRODUCT ? baseProduct.arguments[0] : null;
  if (!productCode) {
    return baseProduct;
  }

  // Apply the template with the base product code
  const mixinExpression = applyArgumentsToTemplate(template, { X: productCode });
  return parseMixin(mixinExpression);
}

/**
 * Converts a UsingEnergySourceExpression to a MixinSyntaxTree using the 'v2-using' operation.
 * The productCode is already set in the expression from ipr-to-logical-expressions.ts.
 */
function convertUsingEnergySource(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.UsingEnergySource) {
    throw new Error(`Expected UsingEnergySource expression, got ${expression.type}`);
  }

  const baseProduct = logicalMixinExpressionToMixinSyntaxTree(expression.baseProduct, templates, options);
  const energySource = logicalMixinExpressionToMixinSyntaxTree(expression.usingEnergySource, templates, options);

  // productCode is already set correctly from ipr-to-logical-expressions.ts
  return createOperation(MixinOperationType.V2_USING, [baseProduct, energySource]);
}

/**
 * Converts a ForSpecialEndUseExpression to a MixinSyntaxTree using the 'v2-for' operation.
 */
function convertForSpecialEndUse(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.ForSpecialEndUse) {
    throw new Error(`Expected ForSpecialEndUse expression, got ${expression.type}`);
  }

  const baseProduct = logicalMixinExpressionToMixinSyntaxTree(expression.baseProduct, templates, options);
  const specialEndUse = logicalMixinExpressionToMixinSyntaxTree(expression.forSpecialEndUse, templates, options);

  return createOperation(MixinOperationType.V2_FOR, [baseProduct, specialEndUse]);
}

/**
 * Converts a RecyclabilityExpression to a MixinSyntaxTree.
 * Note: There's no specific template for recyclability,
 * so this returns the base product as-is for now.
 */
function convertRecyclability(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.Recyclability) {
    throw new Error(`Expected Recyclability expression, got ${expression.type}`);
  }

  // No suitable template found, return base product
  return logicalMixinExpressionToMixinSyntaxTree(expression.baseProduct, templates, options);
}

/**
 * Converts a ForDisabledPeopleExpression to a MixinSyntaxTree using the "X for disabled people" template.
 */
function convertForDisabledPeople(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions,
): MixinSyntaxTree {
  if (expression.type !== ExpressionType.ForDisabledPeople) {
    throw new Error(`Expected ForDisabledPeople expression, got ${expression.type}`);
  }

  const baseProduct = logicalMixinExpressionToMixinSyntaxTree(expression.baseProduct, templates, options);

  const template = templates[FOR_DISABLED_PEOPLE_TEMPLATE_EXPRESSION];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!template) {
    // Return base product if template not found
    return baseProduct;
  }

  // Get the product code from the base product
  const productCode = baseProduct.type === MixinOperandType.PRODUCT ? baseProduct.arguments[0] : null;
  if (!productCode) {
    return baseProduct;
  }

  // Apply the template with the base product code
  const mixinExpression = applyArgumentsToTemplate(template, { X: productCode });
  return parseMixin(mixinExpression);
}

/**
 * Main conversion function that converts a LogicalMixinExpression to a MixinSyntaxTree.
 *
 * This function uses MixinTemplates from the database to form the expression.
 * For industries, packaging materials, and energy sources, it uses static mappings
 * to map from display names to product codes.
 *
 * @param expression The logical mixin expression to convert
 * @param templates A dictionary of mixin templates indexed by their expression (e.g., "FSC certified X")
 *   These templates are typically loaded from the database via getMixinTemplatesByExpression().
 * @returns The corresponding MixinSyntaxTree, or null if conversion is not possible
 *
 * @example
 * // Load templates from the database
 * const templates = await getMixinTemplatesByExpression();
 *
 * // Convert a logical expression
 * const expression: LogicalMixinExpression = {
 *   type: ExpressionType.ForIndustry,
 *   baseProduct: { type: ExpressionType.PureProduct, title: 'Software', productCode: 'SOFTW' },
 *   forProduct: { type: ExpressionType.PureProduct, title: 'Manufacturing' }
 * };
 *
 * const syntaxTree = logicalMixinExpressionToMixinSyntaxTree(expression, templates);
 * // Result: { type: 'v2-for', arguments: [{ type: 'product', arguments: ['SOFTW'] }, { type: 'product', arguments: ['MANUF'] }] }
 */
export function logicalMixinExpressionToMixinSyntaxTree(
  expression: LogicalMixinExpression,
  templates: Dictionary<MixinTemplate>,
  options: LogicalExpressionToMixinSyntaxTreeOptions = {},
): MixinSyntaxTree {
  switch (expression.type) {
    case ExpressionType.PureProduct:
      return convertPureProduct(expression, templates, options);
    case ExpressionType.Combination:
      return convertCombination(expression, templates, options);
    case ExpressionType.ForIndustry:
      return convertForIndustry(expression, templates, options);
    case ExpressionType.Certificate:
      return convertCertificate(expression, templates, options);
    case ExpressionType.UsingEnergySource:
      return convertUsingEnergySource(expression, templates, options);
    case ExpressionType.ForSpecialEndUse:
      return convertForSpecialEndUse(expression, templates, options);
    case ExpressionType.Recyclability:
      return convertRecyclability(expression, templates, options);
    case ExpressionType.ForDisabledPeople:
      return convertForDisabledPeople(expression, templates, options);
    default:
      throw new Error(`Expression type not supported: ${(expression as LogicalMixinExpression).type}`);
  }
}
